import React, { Component } from 'react';
import CustomModal from '../../../util_components/custom_modal';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format, day_format_year, time_format } from '../../../../constants';
import { utc_window } from './filters';
import {
  step_label,
  category_label,
  origin_label,
  origin_tone,
  category_tone,
  tone_style,
  fmt_amount,
  fmt_datetime,
  reason_label,
  student_label
} from './helpers';

// PROD-9152: every event behind one list row. A single failure can be seen by more than one writer
// — our servers, the Stripe webhook and the browser beacon — so each gets its own colour.
function Badge({ tone, children }) {
  return <span style={tone_style(tone)}>{children}</span>;
}

function Field({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-label)',
          marginBottom: 2
        }}
      >
        {label}
      </div>
      <div style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
}

// textAlign is explicit: the global table CSS centres td, which left the body out of step with
// the header.
const cell = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-default)',
  verticalAlign: 'top',
  textAlign: 'left',
  // tableLayout:fixed does not clip — without this a Stripe id just draws over the next column.
  overflowWrap: 'anywhere'
};
const head = {
  ...cell,
  fontSize: 11,
  textTransform: 'uppercase',
  color: 'var(--text-label)'
};

// When holds a date over a time and Details a seti_/pi_ id; Reason takes whatever is left.
const WIDTHS = ['130px', '130px', '110px', 'auto', '110px', '170px'];
// Below this the columns stop being readable, so the table scrolls inside the modal.
const TABLE_MIN_WIDTH = 680;

class EventsModal extends Component {
  state = { loading: true, error: false, events: [] };

  componentDidMount() {
    const { group, date_filter_type, start_date, end_date, iana_timezone } = this.props;

    // The same window the list used. The group key wraps the column, so no index can serve it —
    // without a date bound this query scans the whole table on every icon click.
    const window = utc_window(date_filter_type, start_date, end_date, iana_timezone);
    const qp = new URLSearchParams();
    qp.set('start_date', window.start);
    qp.set('end_date', window.end);

    const url = get_api_url(
      `/v2/cms_user/payment_failures/${encodeURIComponent(group.group_id)}/events?${qp.toString()}`,
      false,
      true
    );

    get_api(url, true)
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        this.setState({ loading: false, error: false, events: data.events || [] });
      })
      .catch((e) => {
        let message;
        try {
          message = e.response.data.message;
        } catch (err) {
          message = 'Some error occured. Please contact dev team.';
        }
        if (typeof message !== 'string') message = 'Some error occured. Please contact dev team.';
        if (this.props.set_notification_variable) {
          this.props.set_notification_variable(true, 'error', message);
        }
        // Distinct from an empty list: "no events" would be a lie when the fetch never landed.
        this.setState({ loading: false, error: true, events: [] });
      });
  }

  // Date over time, so the field stays narrow. A row groups one STUDENT, so their attempts can fall
  // on different days — a bare time would not say which, which is why every row carries its date.
  render_when = (value, day = day_format_year) => (
    <>
      <div>{fmt_datetime(value, this.props.iana_timezone, day)}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {fmt_datetime(value, this.props.iana_timezone, time_format)}
      </div>
    </>
  );

  // The ids differ per event; showing whichever one this event carries keeps the column narrow.
  stripe_ref = (event) =>
    event.stripe_payment_intent_id || event.stripe_setup_intent_id || event.stripe_charge_id || '';

  render_row = (event) => (
    <tr key={event.id}>
      <td style={cell}>{this.render_when(event.occurred_at, day_format)}</td>
      <td style={cell}>
        <div>{step_label(event.step)}</div>
        {fmt_amount(event.amount, event.currency) === '—' ? null : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {fmt_amount(event.amount, event.currency)}
          </div>
        )}
      </td>
      <td style={cell}>
        <Badge tone={origin_tone(event.origin)}>{origin_label(event.origin)}</Badge>
      </td>
      <td style={cell}>
        <div style={{ color: 'var(--text-primary)' }}>{reason_label(event)}</div>
        {event.error_message ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{event.error_message}</div>
        ) : null}
      </td>
      <td style={cell}>
        <Badge tone={category_tone(event.error_category)}>
          {category_label(event.error_category)}
        </Badge>
      </td>
      <td style={{ ...cell, fontSize: 12, color: 'var(--text-muted)' }}>
        {event.error_type ? <div>{event.error_type}</div> : null}
        {event.network_decline_code ? <div>network {event.network_decline_code}</div> : null}
        {event.advice_code ? <div>{event.advice_code}</div> : null}
        {this.stripe_ref(event) ? <div>{this.stripe_ref(event)}</div> : null}
      </td>
    </tr>
  );

  render() {
    const { group, close } = this.props;
    const { loading, error, events } = this.state;

    return (
      <CustomModal show close={close}>
        <div style={{ width: '100%', minWidth: 0, color: 'var(--text-primary)' }}>
          <h4 style={{ marginBottom: 12 }}>Payment failures</h4>

          {/* A grid, not flex+gap: equal tracks keep the labels aligned when one value runs long. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 16,
              padding: 12,
              marginBottom: 16,
              background: 'var(--surface-sunken)',
              borderRadius: 6
            }}
          >
            <Field label="Student">{student_label(group) || 'Not resolved'}</Field>
            <Field label="Country">{group.country || '—'}</Field>
            <Field label="Attempts">{group.event_count}</Field>
            <Field label="First attempt">{this.render_when(group.first_attempt)}</Field>
            <Field label="Last attempt">{this.render_when(group.last_attempt)}</Field>
          </div>

          {loading ? (
            <div>Loading…</div>
          ) : error ? (
            <div style={{ color: 'var(--state-error-text)' }}>
              Could not load the events for this student. This is a load error, not an empty
              timeline.
            </div>
          ) : events.length === 0 ? (
            <div>No events found for this student.</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                {events.length} event{events.length === 1 ? '' : 's'}, newest first
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    minWidth: TABLE_MIN_WIDTH,
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed'
                  }}
                >
                  <colgroup>
                    {WIDTHS.map((w, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={head}>When</th>
                      <th style={head}>Step</th>
                      <th style={head}>Seen by</th>
                      <th style={head}>Reason</th>
                      <th style={head}>Category</th>
                      <th style={head}>Details</th>
                    </tr>
                  </thead>
                  <tbody>{events.map(this.render_row)}</tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </CustomModal>
    );
  }
}

export default EventsModal;
